import { ApolloProvider } from '@apollo/client'
import PartnerBoundary from 'components/boundaries/PartnerBoundary'
import ErrorBoundary from 'lib/boundaries/ErrorBoundary'
import { PosthogManagerProvider } from 'lib/hooks/providers/PosthogManagerProvider'
import MaintenanceMode from 'lib/layouts/MaintenanceMode'
import Root from 'lib/layouts/RootLayout'
import 'react-responsive-modal/styles.css'
import 'lib/ui/DatePicker/styles.css'
import './app.css'
import SegmentScript from 'lib/utils/SegmentScript'
import Seo from 'lib/utils/Seo'
import Zendesk from 'lib/utils/Zendesk'
import type { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import Router from 'next/router'
import setPageName from 'utils/analytics'

import CompanyAndInvoiceProvider from '@/hooks/providers/CompanyAndInvoiceProvider'
import { EvervaultProvider } from '@/hooks/providers/EvervaultProvider'
import { PostHogProvider } from '@/hooks/providers/PosthogProvider'
import { paymentsClient } from '@/utils/apollo'

const Toast = dynamic(() => import('lib/ui/Toast'), {
  ssr: false
})

declare global {
  interface Window {
    analytics: any
  }
}

// Track client-side page views with Segment
Router.events.on('routeChangeComplete', () => {
  window?.analytics?.page(
    setPageName({ route: Router.route, app: 'Payments Portal' })
  )
})

function PaymentsPortal({ Component, pageProps }: AppProps) {
  const segmentKey = process.env.NEXT_PUBLIC_SEGMENT_KEY
  const enableMaintenance = process.env.NEXT_PUBLIC_MAINTENANCE_MODE === 'true'

  return (
    <ApolloProvider client={paymentsClient}>
      <EvervaultProvider>
        <PostHogProvider>
          <PosthogManagerProvider>
            {enableMaintenance ? (
              <Root>
                <MaintenanceMode />
              </Root>
            ) : (
              <CompanyAndInvoiceProvider>
                <Seo />
                <SegmentScript app='Payments Portal' segmentKey={segmentKey} />
                <Toast />
                <Root>
                  <ErrorBoundary>
                    <PartnerBoundary>
                      <Component {...pageProps} />
                    </PartnerBoundary>
                  </ErrorBoundary>
                </Root>
              </CompanyAndInvoiceProvider>
            )}
            <Zendesk />
          </PosthogManagerProvider>
        </PostHogProvider>
      </EvervaultProvider>
    </ApolloProvider>
  )
}

export default PaymentsPortal
